import React, { useState, useEffect } from 'react';
import { User, Calendar, Phone, Mail, MapPin, Book, Lock, Edit, Save, X } from 'lucide-react';
import "../../css/teacher/Profile.css";
import Loader from "../../components/Loader";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

function Profile() {
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    occupation: "",
    relationship: "",
    userCode: "",
    student: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dob: "",
      gender: "",
      address: "",
      parentName: "",
      parentPhone: "",
      grade: "",
      section: "",
      rollNumber: "",
      admissionDate: "",
      bloodGroup: "",
    },
  });

  const [profileImage, setProfileImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({ ...profile });
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  // Define sensitive fields that only admin can edit
  const sensitiveFields = ['email', 'userCode', 'student.email', 'student.rollNumber', 'student.grade', 'student.section', 'student.admissionDate'];

  // Check if a field is sensitive
  const isSensitiveField = (fieldName) => {
    return sensitiveFields.includes(fieldName);
  };

  // Fetch parent profile data
  useEffect(() => {
    fetchParentProfile();
    fetchProfileImage();
  }, []);

  const fetchParentProfile = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized! Please login again.");
        return;
      }

      const response = await fetch("https://schoolpro-api.uniums.com/api/student/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch profile");
      }

      const data = await response.json();
      setProfile(data);
      setEditedProfile(data);
    } catch (error) {
      console.error("Error fetching parent profile:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch profile image
  const fetchProfileImage = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch("https://schoolpro-api.uniums.com/api/student/profile/image", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.profileImage) {
          setProfileImage(data.profileImage);
        }
      }
    } catch (error) {
      console.error("Error fetching profile image:", error);
    }
  };

  // Function to handle profile image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Only JPEG, JPG and PNG images are supported");
      return;
    }

    setIsUploading(true);

    try {
      const reader = new FileReader();

      reader.onloadend = async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            toast.error("Unauthorized! Please login again.");
            setIsUploading(false);
            return;
          }

          const response = await fetch("https://schoolpro-api.uniums.com/api/student/profile/upload-image", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              imageData: reader.result,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to upload image");
          }

          const data = await response.json();
          setProfileImage(data.profileImage);
          toast.success("Profile image updated successfully!");
        } catch (error) {
          console.error("Error uploading image:", error);
          toast.error(`Error: ${error.message}`);
        } finally {
          setIsUploading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error(`Error: ${error.message}`);
      setIsUploading(false);
    }
  };

  // Function to handle profile update
  const saveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized! Please login again.");
        return;
      }

      // Filter out sensitive fields from the update payload
      const updatedData = { student: {} };
      for (const key in editedProfile) {
        if (key !== 'student' && !isSensitiveField(key)) {
          updatedData[key] = editedProfile[key];
        } else if (isSensitiveField(key)) {
          updatedData[key] = profile[key];
        }
      }
      for (const key in editedProfile.student) {
        if (!isSensitiveField(`student.${key}`)) {
          updatedData.student[key] = editedProfile.student[key];
        } else {
          updatedData.student[key] = profile.student[key];
        }
      }

      const response = await fetch("https://schoolpro-api.uniums.com/api/student/profile", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update profile");
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to toggle edit mode
  const toggleEditMode = () => {
    if (isEditing) {
      saveProfile();
    } else {
      setEditedProfile({ ...profile });
      setIsEditing(true);
    }
  };

  // Function to handle field changes in edit mode
  const handleChange = (e, section) => {
    const { name, value } = e.target;

    // Don't update if it's a sensitive field
    const fieldKey = section === 'student' ? `student.${name}` : name;
    if (isSensitiveField(fieldKey)) {
      return;
    }

    if (section === 'student') {
      setEditedProfile({
        ...editedProfile,
        student: {
          ...editedProfile.student,
          [name]: value,
        },
      });
    } else {
      setEditedProfile({
        ...editedProfile,
        [name]: value,
      });
    }
  };

  // Function to cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedProfile({ ...profile });
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Render field based on whether it's sensitive or not
  const renderField = (section, name, label, type = "text", options = null) => {
    const isDateField = type === "date";
    const isSelectField = type === "select";
    const fieldKey = section === 'student' ? `student.${name}` : name;
    const value = section === 'student' ? profile.student[name] : profile[name];
    const editedValue = section === 'student' ? editedProfile.student[name] : editedProfile[name];

    return (
      <div className="teacherprofile-field">
        <label>{label}</label>
        {isEditing ? (
          isSensitiveField(fieldKey) ? (
            <div className="teacherprofile-sensitive-field">
              {isDateField ? formatDate(value) : value}
              <span className="teacherprofile-admin-only">(Admin only)</span>
            </div>
          ) : (
            isSelectField ? (
              <select
                name={name}
                value={editedValue}
                onChange={(e) => handleChange(e, section)}
              >
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : isDateField ? (
              <input
                type="date"
                name={name}
                value={editedValue ? new Date(editedValue).toISOString().split('T')[0] : ''}
                onChange={(e) => handleChange(e, section)}
              />
            ) : type === "textarea" ? (
              <textarea
                name={name}
                value={editedValue}
                onChange={(e) => handleChange(e, section)}
                rows="3"
              ></textarea>
            ) : (
              <input
                type={type}
                name={name}
                value={editedValue}
                onChange={(e) => handleChange(e, section)}
              />
            )
          )
        ) : (
          <div>{isDateField ? formatDate(value) : value}</div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="teacherprofile-container">
      <div className="teacherprofile-header">
        <div className="teacherprofile-header-content">
          <h1>Parent Profile</h1>
          {/* <div className="teacherprofile-actions">
            {isEditing ? (
              <>
                <button className="teacherprofile-button save" onClick={toggleEditMode}>
                  <Save size={18} />
                  Save Changes
                </button>
                <button className="teacherprofile-button cancel" onClick={cancelEditing}>
                  <X size={18} />
                  Cancel
                </button>
              </>
            ) : (
              <button className="teacherprofile-button edit" onClick={toggleEditMode}>
                <Edit size={18} />
                Edit Profile
              </button>
            )}
          </div> */}
        </div>
      </div>

      <div className="teacherprofile-content">
        <div className="teacherprofile-sidebar">
          <div className="teacherprofile-image-container">
            {isUploading ? (
              <div className="teacherprofile-image-loading">
                <Loader />
              </div>
            ) : profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="teacherprofile-image"
              />
            ) : (
              <div className="teacherprofile-image-placeholder">
                <User size={64} />
              </div>
            )}
            <div className="teacherprofile-upload-container">
              <label htmlFor="profile-upload" className="teacherprofile-upload-button">
                {isUploading ? "Uploading..." : "Change Photo"}
              </label>
              <input
                id="profile-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleImageUpload}
                className="teacherprofile-file-input"
                disabled={isUploading}
              />
            </div>
          </div>

          <div className="teacherprofile-info-card">
            <h2 className="teacherprofile-name">
              {profile.firstName} {profile.lastName}
            </h2>
            <div className="teacherprofile-position">{profile.relationship}</div>
            <div className="teacherprofile-id">ID: {profile.userCode}</div>
          </div>

          <div className="teacherprofile-contact-info">
            <h3>Contact Information</h3>
            <div className="teacherprofile-info-item">
              <Mail className="teacherprofile-icon" size={16} />
              <span>{profile.email}</span>
            </div>
            <div className="teacherprofile-info-item">
              <Phone className="teacherprofile-icon" size={16} />
              <span>{profile.phone}</span>
            </div>
            <div className="teacherprofile-info-item">
              <MapPin className="teacherprofile-icon" size={16} />
              <span>{profile.address}</span>
            </div>
          </div>
          <div className="teacherprofile-contact-info">
            <h3>Privacy Information</h3>
            <div className="teacherprofile-info-item">
              <Lock className="teacherprofile-icon" size={16} />
              <Link to={'/student/changePassword'}><b><u>Change Password</u></b></Link>
            </div>
          </div>
        </div>

        <div className="teacherprofile-main">
          <div className="teacherprofile-section">
            <h3>
              <User className="teacherprofile-section-icon" size={18} />
              Parent Information
            </h3>
            <div className="teacherprofile-grid">
              {renderField("parent", "firstName", "First Name")}
              {renderField("parent", "lastName", "Last Name")}
              {renderField("parent", "email", "Email")}
              {renderField("parent", "phone", "Phone Number")}
              {renderField("parent", "occupation", "Occupation")}
              {renderField("parent", "relationship", "Relationship", "select", [
                { value: "father", label: "Father" },
                { value: "mother", label: "Mother" },
                { value: "guardian", label: "Guardian" },
              ])}
              {renderField("parent", "userCode", "User Code")}
            </div>
          </div>

          <div className="teacherprofile-section">
            <h3>
              <User className="teacherprofile-section-icon" size={18} />
              Student Information
            </h3>
            <div className="teacherprofile-grid">
              {renderField("student", "firstName", "First Name")}
              {renderField("student", "lastName", "Last Name")}
              {renderField("student", "email", "Email")}
              {renderField("student", "phone", "Phone Number")}
              {renderField("student", "dob", "Date of Birth", "date")}
              {renderField("student", "gender", "Gender", "select", [
                { value: "male", label: "Male" },
                { value: "female", label: "Female" },
                { value: "other", label: "Other" },
              ])}
              {renderField("student", "bloodGroup", "Blood Group", "select", [
                { value: "A+", label: "A+" },
                { value: "A-", label: "A-" },
                { value: "B+", label: "B+" },
                { value: "B-", label: "B-" },
                { value: "AB+", label: "AB+" },
                { value: "AB-", label: "AB-" },
                { value: "O+", label: "O+" },
                { value: "O-", label: "O-" },
              ])}
            </div>
          </div>

          <div className="teacherprofile-section">
            <h3>
              <Book className="teacherprofile-section-icon" size={18} />
              Academic Information
            </h3>
            <div className="teacherprofile-grid">
              {renderField("student", "grade", "Grade")}
              {renderField("student", "section", "Section")}
              {renderField("student", "rollNumber", "Roll Number")}
              {renderField("student", "admissionDate", "Admission Date", "date")}
              {renderField("student", "parentName", "Parent Name")}
              {renderField("student", "parentPhone", "Parent Phone")}
            </div>
          </div>

          <div className="teacherprofile-section">
            <h3>
              <MapPin className="teacherprofile-section-icon" size={18} />
              Address
            </h3>
            <div className="teacherprofile-grid">
              <div className="teacherprofile-field full-width">
                <label>Parent Address</label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={editedProfile.address}
                    onChange={(e) => handleChange(e, 'parent')}
                    rows="3"
                  ></textarea>
                ) : (
                  <div>{profile.address}</div>
                )}
              </div>
              <div className="teacherprofile-field full-width">
                <label>Student Address</label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={editedProfile.student.address}
                    onChange={(e) => handleChange(e, 'student')}
                    rows="3"
                  ></textarea>
                ) : (
                  <div>{profile.student.address}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;